import { useState, useCallback } from 'react';

const MAX_STACK = 100;

interface UndoRedoState<T> {
  past: T[];
  present: T;
  future: T[];
}

/**
 * A custom hook that provides undo and redo functionality for a state value.
 * @param initialPresent The initial state value.
 * @returns An object containing the present state, and functions to set, undo, redo,
 * and reset the state, along with boolean flags indicating if undo and redo are possible.
 */
export function useUndoRedo<T>(initialPresent: T) {
  const [state, setState] = useState<UndoRedoState<T>>({
    past: [],
    present: initialPresent,
    future: [],
  });

  const canUndo = state.past.length > 0;
  const canRedo = state.future.length > 0;

  const set = useCallback(
    (newPresent: T) => {
      setState((currentState) => {
        let resultState = null;
        if (
          currentState.present !== null &&
          currentState.present !== undefined &&
          Object.keys(currentState.present).length > 0
        ) {
          // if present is not null/empty, push it to past
          const newPast = [...currentState.past, currentState.present];
          const cappedPast = newPast.length > MAX_STACK ? newPast.slice(newPast.length - MAX_STACK) : newPast;
          resultState = {
            past: cappedPast,
            present: newPresent,
            future: [],
          };
        } else {
          resultState = {
            past: [],
            present: newPresent,
            future: [],
          };
        }
        return resultState;
      });
    },
    [setState]
  );

  const undo = useCallback(() => {
    setState((currentState) => {
      if (currentState.past.length === 0) return currentState;

      const previous = currentState.past[currentState.past.length - 1];
      const newPast = currentState.past.slice(0, currentState.past.length - 1);

      const newFutureUncapped = [currentState.present, ...currentState.future];
      let newFuture = newFutureUncapped;
      if (newFutureUncapped.length > MAX_STACK) {
        newFuture = newFutureUncapped.slice(0, MAX_STACK);
      }

      return {
        past: newPast,
        present: previous,
        future: newFuture,
      };
    });
  }, [setState]);

  const redo = useCallback(() => {
    setState((currentState) => {
      if (currentState.future.length === 0) return currentState;

      const next = currentState.future[0];
      const newFuture = currentState.future.slice(1);

      const newPastUncapped = [...currentState.past, currentState.present];
      let newPast = newPastUncapped;
      if (newPastUncapped.length > MAX_STACK) {
        newPast = newPastUncapped.slice(newPastUncapped.length - MAX_STACK);
      }

      return {
        past: newPast,
        present: next,
        future: newFuture,
      };
    });
  }, [setState]);

  const reset = useCallback(
    (newPresent: T) => {
      setState({
        past: [],
        present: newPresent,
        future: [],
      });
    },
    [setState]
  );

  return { present: state.present, set, undo, redo, reset, canUndo, canRedo };
}

export default useUndoRedo;
